<template>
  <div>
    <Errorpage v-if="error" :msg="error" />
    <NotFound v-if="!log && !error" />
    <div class="row" v-if="log">

      <div class="col-md-4 log-details">
        <h6>Log Details</h6>

        <h4>{{ log.Name }}</h4>
        <p>
          <span v-if="log.Endpoint == '(Private Log)'">{{ log.Endpoint }}</span><a v-else :href="'https://'+log.Endpoint">{{ log.Endpoint }}</a><br />
          <span><u>Last Update:</u> {{ sprintDate(log.LastUpdate) }}</span><br />
          <span><u>Avg. Throughput:</u> {{ throughput.toLocaleString() }} cert<template v-if="throughput != 1">s</template>/hr</span><br />
          <span><u>Contains:</u> {{ log.Count.toLocaleString() }} certificate<template v-if="log.Count != 1">s</template></span><br />
          <span><u>Unsubmitted:</u> {{ log.Unsubmitted.toLocaleString() }} certificate<template v-if="log.Unsubmitted != 1">s</template> ({{ sprintSmallPercent(log.Count / (log.Count + log.Unsubmitted)) }}&nbsp;full)</span>

          <p v-if="atCapacity && velocity.length > 0">This log is operating at full capacity.</p>

          <p v-if="velocity.length == 0"><strong>This log is frozen.</strong> It
          accepts no new entries and signs no new STHs but continues to operate
          in read-only mode.</p>

        <h6 class="sub-header">Uptime</h6>
        <h5>{{ sprintPercent(log.Profile.Uptime) }}</h5>
        <table class="table table-sm table-hover">
          <thead class="sr-only">
            <tr>
              <th scope="col">Endpoint</th>
              <th class="text-right" scope="col">Uptime</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ep in endpoints">
              <td>{{ ep.name }}</td>
              <td class="text-right" v-if="log.Profile.ByEndpoint[ep.tag].Uptime == -1">N/A</td>
              <td class="text-right" v-if="log.Profile.ByEndpoint[ep.tag].Uptime != -1">{{ sprintPercent(log.Profile.ByEndpoint[ep.tag].Uptime) }}</td>
            </tr>
          </tbody>
        </table>

        <h6 class="sub-header">Response Time</h6>
        <h5>{{ sprintDuration(log.Profile.ResponseTime) }}</h5>
        <table class="table table-sm table-hover">
          <thead class="sr-only">
            <tr>
              <th scope="col">Endpoint</th>
              <th class="text-right" scope="col">Resp. Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ep in endpoints">
              <td>{{ ep.name }}</td>
              <td class="text-right" v-if="log.Profile.ByEndpoint[ep.tag].ResponseTime == -1">N/A</td>
              <td class="text-right" v-if="log.Profile.ByEndpoint[ep.tag].ResponseTime != -1">{{ sprintDuration(log.Profile.ByEndpoint[ep.tag].ResponseTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="col-md-8 log-graphs">
        <div class="row" v-if="velocity.length > 0">
          <div class="col-md-12">
            <h6>Log Velocity</h6>

            <VelocityGraph id="velocity" :data="velocity" />
            <h6 class="sub-header">Certificates Seen vs. Tree Growth</h6>
          </div>
        </div>

        <h6>Contents Breakdown</h6>

        <div class="row">
          <Filters :breakdownFilters="breakdownFilters" :unselect="unselect" />
        </div>

        <div class="row">
          <div class="col-md-4">
            <PieGraph id="vallevel" :data="breakdown['valLevel']" :select="select('valLevel')" />
            <h6 class="sub-header">Validation Level</h6>
          </div>
          <div class="col-md-4">
            <PieGraph id="pubalg" :data="breakdown['pubAlg']" :select="select('pubAlg')" />
            <h6 class="sub-header">Public Key Algorithm</h6>
          </div>
          <div class="col-md-4">
            <PieGraph id="sigalg" :data="breakdown['sigAlg']" :select="select('sigAlg')" />
            <h6 class="sub-header">Signature Algorithm</h6>
          </div>
        </div>

        <div class="row">
          <div class="col-md-2">&nbsp;</div>
          <div class="col-md-4">
            <PieGraph id="entrytype" :data="breakdown['entryType']" :select="select('entryType')" />
            <h6 class="sub-header">Entry Type</h6>
          </div>
          <div class="col-md-4">
            <PieGraph id="expired" :data="breakdown['expired']" :select="select('expired')" />
            <h6 class="sub-header">Expired vs. Current</h6>
          </div>
          <div class="col-md-2">&nbsp;</div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <CompGraph id="root" :data="breakdown['rootName']" :select="select('rootName')" />
            <h6 class="sub-header">Root Certificate Authorities</h6>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
  import * as roll from '@/components/mixins/roll'
  import { default as utilMixin, loadData } from '@/components/mixins/util'

  import Filters from '@/components/widgets/Filters'
  import CompGraph from '@/components/widgets/CompGraph'
  import PieGraph from '@/components/widgets/PieGraph'
  import VelocityGraph from '@/components/widgets/VelocityGraph'
  import Errorpage from '@/components/widgets/Errorpage'
  import NotFound from '@/components/NotFound'

  export default {
    name: 'Logpage',
    beforeRouteEnter: function(to, from, next) { loadData('/logs/'+to.params.name, next) },
    beforeRouteUpdate: function(to, from, next) { loadData('/logs/'+to.params.name, next) },

    data: function() {
      return {
        error: null,
        log: null,

        breakdownFilters: {},

        endpoints: [
          {tag: 'add-chain-new', name: 'add-chain (new)'},
          {tag: 'add-chain-old', name: 'add-chain (old)'},
          {tag: 'add-pre-chain-new', name: 'add-pre-chain (new)'},
          {tag: 'add-pre-chain-old', name: 'add-pre-chain (old)'},
          {tag: 'get-entries', name: 'get-entries'},
          {tag: 'get-roots', name: 'get-roots'},
          {tag: 'get-sth', name: 'get-sth'},
        ],
      }
    },
    computed: {
      breakdown: function() { return roll.parse(this.log.Breakdown, this.breakdownFilters) },

      atCapacity: function() {
        return this.log.Name == 'DigiCert 1' || this.log.Name == 'DigiCert 2'
      },

      velocity: function() {
        return this.log.Velocity.map(function(v) {
          return {start: v.Start, end: v.End, added: v.Added, seen: v.Seen}
        })
      },
      throughput: function() {
        if (this.velocity.length == 0) {
          return 0
        }

        const hourInMs = 3600 * 1000
        let total = this.velocity.reduce(function(n, d) { return n + d.added}, 0),
          extent = this.velocity[this.velocity.length-1].end - this.velocity[0].start
        return Math.round(total / (extent / hourInMs))
      },
    },
    methods: {
      setData: function(err, data) {
        if (err) {
          this.error = err.toString()
          if (this.error == 'Error: Request failed with status code 404') {
            this.error = null
          }
        } else {
          this.log = data
        }
      },
      select: function(field) {
        let self = this
        return function(image) {
          let filters = Object.assign({}, self.breakdownFilters)
          filters[field] = image
          self.breakdownFilters = filters
        }
      },
      unselect: function(field) {
        let filters = Object.assign({}, this.breakdownFilters)
        delete filters[field]
        this.breakdownFilters = filters
      },
    },
    components: { Filters, CompGraph, PieGraph, VelocityGraph, Errorpage, NotFound },
    mixins: [utilMixin],
  }
</script>

<style scoped>
  div.log-details a {
    color: DodgerBlue;
  }
  div.log-details h6.sub-header {
    border-bottom: none;
    margin-bottom: 0.5rem;
    padding-bottom: 0rem;
  }
  div.log-graphs h6.sub-header {
    padding: 0.1rem;
    margin: 0;
    border-bottom: none;
    text-align: center;
  }
  div.log-graphs div.row {
    margin-bottom: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/Logpage.vue